import { FormEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  useStripe,
  useElements,
  PaymentElement,
  LinkAuthenticationElement,
} from '@stripe/react-stripe-js'
import {
  StripeLinkAuthenticationElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js'

import { TGetItemFn } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { PaymentMethodService } from '@/enums/paymentMethodServices'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { LTPPaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { getPath } from '@/utils/urlUtils'

import { PrimaryButtonWithIcon } from '@/components/buttons/primaryButton'

import { ApiService } from '@/api'
import { TPurchaseContentPayload } from '@/api/types'
import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import {
  trackLTPPaymentClick,
  trackLTPPaymentInfoAdded,
  trackLTPPurchaseStarted,
} from '@/events/events'

import { TLTPStripeCheckoutFormProps } from './types'
import { InputErrorMessage } from '../inputErrorMessage'
import { PaymentMethodIcons } from '../paymentMethodIcons'

const options: StripePaymentElementOptions = {
  fields: {
    billingDetails: {
      email: 'auto',
      address: { country: 'never' },
    },
  },
  terms: { card: 'never' },
  wallets: {
    applePay: 'never',
    googlePay: 'never',
  },
}

const getResultUrl = (paymentResultUrl: string, successPage: PageName): URL => {
  const fail = PageName.ltpPaymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.set('success_page', successPage)
  url.searchParams.set('fail_page', fail)
  url.searchParams.set('payment_method', PaymentMethod.CARD)

  return url
}

function prepareContentPayload(
  getItem: TGetItemFn,
  email: string,
  sessionContext: ISessionContext,
  config: IConfig,
  contentPriceId: string
): TPurchaseContentPayload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  return {
    session_id: sessionId,
    page_set_id: config.PAGE_SET_ID,
    email: email,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    internal_content_price_id: contentPriceId,
  }
}

export const LTPStripeCheckoutFormV2 = ({
  contentDetails,
  getItem,
  setItem,
  config,
  formConfig,
  successRedirectTo,
  pageName,
}: TLTPStripeCheckoutFormProps) => {
  const navigate = useNavigate()
  const sessionContext = useSession()
  const { t } = useTranslation()
  const { user } = useAuth()

  const stripe = useStripe()
  const elements = useElements()

  const [isFormLoading, setIsFormLoading] = useState<boolean>(true) // will be false when stripe is ready
  const [isLinkAuthElLoading, setIsLinkAuthElLoading] = useState<boolean>(true) // will be false when stripe is ready

  const isLoading = isFormLoading || isLinkAuthElLoading

  const [email, setEmail] = useState<string>(() => getItem(emailKey) || '')
  const [requestError, setRequestError] = useState<string>()

  const paymentMethodsOrder = formConfig?.paymentMethodsSection?.order || [
    PaymentMethodService.VISA_BW,
    PaymentMethodService.MASTERCARD_BW,
    PaymentMethodService.MAESTRO_BW,
    PaymentMethodService.DISCOVER_BW,
  ]

  const showPaymentMethodsIcons =
    LTPPaymentScreenVersion.default === config.LTP_PAYMENT_SCREEN_VERSION

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!stripe || !elements || !email) {
      // Stripe.js hasn't yet loaded.
      return
    }

    setIsFormLoading(true)

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setIsFormLoading(false)
      return
    }

    setItem('ltpPaymentMethod', PaymentMethod.CARD)
    setItem('ltpPaymentPlatform', PaymentPlatform.STRIPE)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [
      trackLTPPurchaseStarted(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      ),
    ]

    setItem(PageName.ltpPaymentScreen, '')

    promisesList.push(
      trackLTPPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        user,
        [],
        ['card']
      )
    )
    promisesList.push(
      trackLTPPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      )
    )

    const redirectToResultPage = async (paymentResultUrl: string, clientSecret: string) => {
      const redirectTo = getResultUrl(paymentResultUrl, successRedirectTo)
      const intent = await stripe.retrievePaymentIntent(clientSecret)
      if (intent.error) {
        navigate(getPath(PageName.ltpPaymentFailed))
        return
      }
      redirectTo.searchParams.set('payment_intent', intent.paymentIntent.id)
      Promise.all(promisesList).finally(() => {
        setTimeout(function () {
          document.location.href = redirectTo.href
        }, 100)
      })
    }

    const payload = prepareContentPayload(
      getItem,
      email,
      sessionContext,
      config,
      contentDetails.internalPriceId
    )

    let response
    try {
      resetContactStateCheckTimeout(setItem)
      response = await ApiService.purchaseContent(payload)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('subscribe error', error)

      if (error && error.response) {
        if (error.response.status === 400) {
          if (error.response.data?.email === 'Invalid email') {
            setItem(emailKey, '')
            setRequestError(
              t('registrationPage.errorMessages.invalidEmail', '') || 'Please, enter a valid email'
            )

            setIsFormLoading(false)
            return
          }
        } else if (error.response.status === 409) {
          navigate(getPath(successRedirectTo))
          return
        }
        if (error.response.data?.email) {
          setItem(emailKey, error.response.data.email)
        }
      }

      navigate(getPath(PageName.ltpPaymentFailed))
      return
    }

    const {
      client_secret: clientSecret,
      country,
      payment_result_url: paymentResultUrl,
      email: responseEmail,
    } = response.data

    setItem(emailKey, responseEmail)

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getResultUrl(paymentResultUrl, successRedirectTo).href,
          payment_method_data: {
            billing_details: {
              address: {
                country: country?.toUpperCase() || 'US',
              },
            },
          },
        },
      })

      if (error) {
        // This point is only reached if there's an immediate error when confirming the Intent.
        // Show the error to your customer (for example, "payment details incomplete").
        console.log('error2', error)
        redirectToResultPage(paymentResultUrl, clientSecret)
        return
      } else {
        // Your customer is redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer is redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    } catch (e) {
      redirectToResultPage(paymentResultUrl, clientSecret)
    }
  }

  function onStripeFormReady() {
    setIsFormLoading(false)
  }

  function onLinkauthElementReady() {
    setIsLinkAuthElLoading(false)
  }

  function onEscape() {
    console.log('escape')
  }

  const onEmailChange = useCallback(
    async function (event: StripeLinkAuthenticationElementChangeEvent) {
      if (event.complete) {
        setEmail(event.value.email)
      } else if (email) {
        setEmail('')
      }
      if (requestError) {
        setRequestError('')
      }
    },
    [setEmail, setRequestError, email, requestError]
  )

  return (
    <div className="stripe-checkout-form-container">
      {showPaymentMethodsIcons && <PaymentMethodIcons order={paymentMethodsOrder} />}
      <form onSubmit={onSubmit}>
        <PaymentElement options={options} onReady={onStripeFormReady} />
        <LinkAuthenticationElement
          options={{ defaultValues: { email } }}
          onReady={onLinkauthElementReady}
          onEscape={onEscape}
          onChange={onEmailChange}
        />
        <InputErrorMessage rowNumber={1} message={requestError} />
        <div className="button-container">
          <PrimaryButtonWithIcon
            isLoading={isLoading}
            IconComponent={formConfig?.PaymentButtonIconComponent}
            disabled={!email}
            type="submit"
            style={{ textTransform: 'none' }}
            text={formConfig?.paymentButtonText || 'Continue purchase'}
          />
        </div>
      </form>
    </div>
  )
}
